import React, { useState, useEffect, useCallback, useRef } from "react";
import { API_URL, getAuthHeaders } from "../utils/api";
import { useAuth } from "../context/AuthContext";

function getISOWeekNumber(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}

// Local YYYY-MM-DD (NOT toISOString — that's UTC, which would roll over at
// the wrong instant for anyone west of UTC and re-show the message a few
// hours early on the user's clock).
function localDateKey(date = new Date()) {
  return date.toLocaleDateString("en-CA");
}

function DailyMessage() {
  const { user } = useAuth() || {};
  const storageKey = `chariot-daily-msg-dismissed:${user?.email || "default"}`;

  const [message, setMessage] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(storageKey) === localDateKey(); }
    catch { return false; }
  });

  // Tracks the local calendar date the last fetched message belongs to. When
  // midnight rolls over (or the laptop wakes on a new day), this lags `today`
  // and we know to refetch.
  const lastFetchedDayRef = useRef(localDateKey());

  const fetchMessage = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/admin/daily-messages`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) return;
      const data = await res.json();

      const today = new Date();
      const dayOfWeek = today.getDay();
      const messagesForToday = (data.data?.[dayOfWeek] || [])
        .slice()
        .sort((a, b) => a.sort_order - b.sort_order);

      lastFetchedDayRef.current = localDateKey(today);

      if (messagesForToday.length === 0) { setMessage(null); return; }

      const weekNum = getISOWeekNumber(today);
      const index = weekNum % messagesForToday.length;
      setMessage(messagesForToday[index].message);
    } catch {
      // silent — don't break the UI if this fails
    }
  }, []);

  useEffect(() => { fetchMessage(); }, [fetchMessage]);

  // Re-sync dismissed flag whenever the user changes (login, view-as).
  useEffect(() => {
    try { setDismissed(localStorage.getItem(storageKey) === localDateKey()); }
    catch { setDismissed(false); }
  }, [storageKey]);

  // Midnight rollover — runs on a 60s tick, on tab focus, and on visibility
  // change. Independent of whether the user dismissed: even if they never
  // clicked X, we must refetch when the calendar day changes so the new
  // day's message (different day_of_week) replaces yesterday's.
  useEffect(() => {
    const rollOverIfNewDay = () => {
      const today = localDateKey();
      let stored = null;
      try { stored = localStorage.getItem(storageKey); } catch (_) { /* ignore */ }
      // Stale dismissal from a prior day — clear it.
      if (stored && stored !== today) {
        try { localStorage.removeItem(storageKey); } catch (_) { /* ignore */ }
        setDismissed(false);
      }
      // Day has rolled since we last fetched — get the new day's message.
      if (lastFetchedDayRef.current !== today) {
        fetchMessage();
      }
    };
    const id = setInterval(rollOverIfNewDay, 60_000);
    const onVisible = () => { if (!document.hidden) rollOverIfNewDay(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", rollOverIfNewDay);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", rollOverIfNewDay);
    };
  }, [fetchMessage, storageKey]);

  // Cross-tab sync: if the user dismisses in tab A, tab B picks it up via
  // the `storage` event without waiting for focus.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== storageKey) return;
      setDismissed(e.newValue === localDateKey());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [storageKey]);

  const handleDismiss = () => {
    try { localStorage.setItem(storageKey, localDateKey()); }
    catch (_) { /* ignore */ }
    setDismissed(true);
  };

  if (!message || dismissed) return null;

  return (
    <div className="daily-message" role="status" aria-live="polite">
      <span className="daily-message-icon" aria-hidden="true">⚡</span>
      <span className="daily-message-text">{message}</span>
      <button
        type="button"
        className="daily-message-close"
        onClick={handleDismiss}
        aria-label="Dismiss for today"
        title="Dismiss for today"
      >
        ×
      </button>
    </div>
  );
}

export default DailyMessage;
